import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ADMIN_MENU, type AdminMenuItem } from './adminMenu';

interface AdminSidebarProps {
  onNavigate?: () => void;
}

export default function AdminSidebar({ onNavigate }: AdminSidebarProps) {
  const location = useLocation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ content: true });

  const isPathActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const isItemActive = (item: AdminMenuItem) =>
    isPathActive(item.path);

  const isGroupActive = (item: AdminMenuItem) => {
    if (isItemActive(item)) return true;
    if (item.children) {
      return item.children.some((child) => isItemActive(child));
    }
    return false;
  };

  const toggleGroup = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="h-16 shrink-0 flex items-center gap-2.5 px-5 border-b border-stone-100">
        <div className="w-8 h-8 rounded-lg bg-amber-600 text-white flex items-center justify-center font-bold text-sm">
          G
        </div>
        <div className="leading-tight">
          <div className="text-sm font-bold text-stone-900">GBCOSPACK</div>
          <div className="text-[10px] text-stone-400 font-medium tracking-wider uppercase">
            Admin Console
          </div>
        </div>
      </div>

      {/* Menu tree */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {ADMIN_MENU.map((item) => {
          if (item.children && item.children.length > 0) {
            const isOpen = expanded[item.id] !== false;
            const groupActive = isGroupActive(item);

            return (
              <div key={item.id}>
                <div className="flex items-center justify-between rounded-lg hover:bg-stone-50 transition-colors">
                  <a
                    href={item.path}
                    onClick={() => onNavigate?.()}
                    className={`flex-1 flex items-center gap-2.5 px-3 py-2 rounded-l-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      groupActive
                        ? 'text-amber-700'
                        : 'text-stone-700 hover:text-stone-900'
                    }`}
                  >
                    <i
                      className={`${item.icon} text-base ${
                        groupActive ? 'text-amber-600' : 'text-stone-400'
                      }`}
                    />
                    <span className="flex-1 text-left">{item.label}</span>
                  </a>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleGroup(item.id);
                    }}
                    className="shrink-0 w-7 h-7 flex items-center justify-center rounded-md text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors cursor-pointer"
                  >
                    <i
                      className={`${
                        isOpen ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'
                      } text-sm`}
                    />
                  </button>
                </div>
                {isOpen && (
                  <div className="mt-1 ml-4 pl-3 border-l border-stone-100 space-y-0.5">
                    {item.children.map((child) => {
                      const active = isItemActive(child);
                      return (
                        <a
                          key={child.id}
                          href={child.path}
                          onClick={() => onNavigate?.()}
                          className={`group flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
                            active
                              ? 'bg-amber-50 text-amber-700 font-medium'
                              : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                          }`}
                        >
                          <i
                            className={`${child.icon} text-base ${
                              active
                                ? 'text-amber-600'
                                : 'text-stone-400 group-hover:text-stone-600'
                            }`}
                          />
                          <span className="flex-1 text-left">{child.label}</span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          const active = isItemActive(item);
          return (
            <a
              key={item.id}
              href={item.path}
              onClick={() => onNavigate?.()}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                active
                  ? 'bg-amber-50 text-amber-700'
                  : 'text-stone-700 hover:bg-stone-50 hover:text-stone-900'
              }`}
            >
              <i
                className={`${item.icon} text-base ${
                  active ? 'text-amber-600' : 'text-stone-400'
                }`}
              />
              <span className="flex-1 text-left">{item.label}</span>
            </a>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="shrink-0 p-4 border-t border-stone-100">
        <div className="rounded-lg bg-stone-50 px-3 py-2.5">
          <p className="text-[11px] text-stone-400 leading-relaxed">
            사이트 콘텐츠, 고객, 견적 템플릿을 한곳에서 관리할 수 있어요.
          </p>
        </div>
      </div>
    </div>
  );
}